import { ExpandMoreRounded } from '@mui/icons-material';
import { Menu, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { skipToken } from '@reduxjs/toolkit/query';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { AdminRoutes, Modals, NavRoutes, RouteType } from '@/constants';
import { IncompleteNotificationBadge } from '@/containers/IncompleteNotificationBadge';
import { Link, LinkVariants } from '@/containers/Link';
import { EVENT_CONSTANTS } from '@/services';
import {
  selectIsAdminUser,
  selectSessionUserId,
  uiActions,
  useAppDispatch,
  useFetchBillingAdminOrgsQuery,
  useLazyFetchOrgBillingPortalLinkQuery,
} from '@/store';
import { SnackbarUtils } from '@/utils';

export const NavItems = () => {
  const dispatch = useAppDispatch();
  const isUserManagementAdmin = useSelector(selectIsAdminUser);
  const userId = useSelector(selectSessionUserId);
  const adminMenuRef = useRef(null);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const openSignupModal = (route: RouteType) => {
    dispatch(
      uiActions.setActiveModal({
        name: Modals.SIGN_UP,
        params: {
          referrer: { component: EVENT_CONSTANTS.COMPONENT.NAV_BAR },
          redirect: route.PATH,
        },
      }),
    );
  };

  const [fetchBillingPortalLink, { isLoading: isFetchingBillingPortalLink }] =
    useLazyFetchOrgBillingPortalLinkQuery();
  const { data: billingAdminOrgs } = useFetchBillingAdminOrgsQuery(
    userId ? undefined : skipToken,
  );
  const isBillingAdmin = (billingAdminOrgs?.length ?? 0) > 0;

  return (
    <Box sx={styles.container}>
      {Object.values(NavRoutes).map(route => {
        const isPrivateAndUnauthed = route.IS_PRIVATE && !userId;
        const isActive = !!matchPath(
          { path: route.PATH, end: false },
          pathname,
        );

        return (
          <Link
            key={route.PATH}
            to={isPrivateAndUnauthed ? undefined : route.PATH}
            onClick={
              isPrivateAndUnauthed ? () => openSignupModal(route) : undefined
            }>
            <NavNotificationsBadge
              showBadge={route.NAME === NavRoutes.MY_LUMIQ.NAME}>
              <Typography
                variant="h5"
                sx={[styles.navLink, isActive && styles.activeNavLink]}>
                {route.NAME}
              </Typography>
            </NavNotificationsBadge>
          </Link>
        );
      })}
      {isUserManagementAdmin && (
        <>
          <Box>|</Box>
          <Box
            ref={adminMenuRef}
            sx={{ cursor: 'pointer' }}
            aria-haspopup="true"
            aria-controls="admin-menu"
            aria-label="Admin Menu"
            onClick={() => setIsAdminMenuOpen(true)}>
            <Typography variant="h5" sx={styles.navLink}>
              Admin
              <ExpandMoreRounded
                sx={{ height: 20, width: 20, verticalAlign: 'text-bottom' }}
              />
            </Typography>
          </Box>
        </>
      )}
      <Menu
        anchorEl={adminMenuRef.current}
        keepMounted
        open={isAdminMenuOpen}
        role="menu"
        onClose={() => setIsAdminMenuOpen(false)}
        slotProps={{
          paper: {
            sx: styles.navMenu,
          },
        }}>
        {Object.values(AdminRoutes).map(route => (
          <Link
            key={route.PATH}
            to={route.PATH}
            variant={LinkVariants.PLAIN}
            onClick={() => setIsAdminMenuOpen(false)}>
            <Typography
              variant="h6"
              sx={[
                styles.navLink,
                styles.menuLink,
                pathname === route.PATH && styles.activeNavLink,
              ]}>
              {route.NAME}
            </Typography>
          </Link>
        ))}
        {isBillingAdmin && (
          <Link
            variant={LinkVariants.PLAIN}
            onClick={async () => {
              if (isFetchingBillingPortalLink) return;
              try {
                const billingPortalLink = await fetchBillingPortalLink({
                  organizationId: billingAdminOrgs![0],
                  redirectUrl: window.location.href,
                }).unwrap();
                window.open(billingPortalLink, '_self');
              } catch {
                SnackbarUtils.error(
                  'Failed to open billing portal. Please contact support.',
                );
              }
              setIsAdminMenuOpen(false);
            }}>
            <Typography variant="h6" sx={[styles.navLink, styles.menuLink]}>
              Billing
            </Typography>
          </Link>
        )}
      </Menu>
    </Box>
  );
};

const NavNotificationsBadge = ({
  children,
  showBadge,
}: {
  children: React.ReactNode;
  showBadge?: boolean;
}) => {
  if (!showBadge) {
    return children;
  }

  return <IncompleteNotificationBadge>{children}</IncompleteNotificationBadge>;
};

const styles = {
  container: {
    display: { xs: 'none', lg: 'flex' },
    alignItems: 'center',
    gap: 4,
    ml: 4,
    color: 'grey100',
    'a, button': {
      color: 'inherit',
    },
    'a:hover, button:hover': {
      textDecoration: 'none',
      color: 'text.contrast',
    },
  },
  navMenu: {
    bgcolor: 'primary.main',
    mt: 2,
    ml: -2,
  },
  navLink: {
    color: 'grey100',
    transition: 'color 300ms',
    '&:hover': { color: 'text.contrast' },
  },
  activeNavLink: {
    color: 'text.contrast',
    cursor: 'default',
  },
  menuLink: {
    px: 2,
    py: 1,
  },
};
